// Prototype data: an in-memory Fellowship with a handful of fictional friends, so every screen can
// be seen and used before the API has social routes. Writes live until the page reloads. Names and
// people are invented; Scripture is the World English Bible (public domain); the Owen passage is
// from his Works (public domain).
import { lookupWeb } from "./bible";
import { ApiError } from "./problem";
import { lookupPassage } from "./passages";
import type { FellowshipSource, Session } from "./source";
import {
  LIMITS,
  NO_DETAILS,
  type Comment,
  type Device,
  type ImageUpload,
  type PostImage,
  type Friends,
  type Notification,
  type Post,
  type Profile,
  type ProfileDetails,
  type ProfileDetailsInput,
  type ProfileOptions,
  type ProfileSettings,
  type Reaction,
  type SocialProvider,
  type UserCard,
  type Votd,
} from "./types";

/** The API's GET /v1/profile-options, as of 2026-10-14 (the tradition list is provisional until Eddy curates it). */
export const PROFILE_OPTIONS: ProfileOptions = {
  traditions: [
    { key: "evangelical-protestant", label: "Evangelical Protestant" },
    { key: "mainline-protestant", label: "Mainline Protestant" },
    { key: "historically-black-protestant", label: "Historically Black Protestant" },
    { key: "catholic", label: "Catholic" },
    { key: "orthodox", label: "Orthodox" },
    { key: "latter-day-saint", label: "Latter-day Saint" },
    { key: "jehovahs-witness", label: "Jehovah's Witness" },
    { key: "other-christian", label: "Another Christian tradition" },
    { key: "jewish", label: "Jewish" },
    { key: "muslim", label: "Muslim" },
    { key: "buddhist", label: "Buddhist" },
    { key: "hindu", label: "Hindu" },
    { key: "unaffiliated", label: "None in particular" },
    { key: "other", label: "Other" },
  ],
  visibilities: ["friends", "private"],
  defaults: { locationVisibility: "friends", faithVisibility: "private" },
  limits: { city: 80, denomination: 80, church: 100 },
  faithConsentText:
    "Your tradition, denomination and church are sensitive information. Bible Standard keeps them only because you choose to add them, shows them only to the people you pick, and deletes them when you clear them or delete your account.",
};

const daysAgoIso = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();

const H = 60 * 60 * 1000;
const ago = (ms: number) => new Date(Date.now() - ms).toISOString();
const delay = <T>(v: T, ms = 120): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));

const user = (id: string, handle: string, displayName: string): UserCard => ({ id, handle, displayName, avatarUrl: null });

const me = user("u-me", "samuel", "Samuel Hart");
const ruth = user("u-ruth", "ruth", "Ruth Adeyemi");
const tom = user("u-tom", "tom", "Thomas Bell");
const grace = user("u-grace", "grace", "Grace Mensah");
const peter = user("u-peter", "peter", "Peter Lindqvist");
const hannah = user("u-hannah", "hannah", "Hannah Price");
const mark = user("u-mark", "mark", "Mark Ellis");

const zero = { amen: 0, praying: 0, love: 0, insight: 0 };

function post(p: Partial<Post> & Pick<Post, "id" | "author" | "kind" | "createdAt">): Post {
  const mine = p.author.id === me.id;
  return {
    visibility: "friends",
    body: null,
    title: null,
    ref: null,
    translation: null,
    quote: null,
    work: null,
    source: null,
    votdKey: null,
    reactions: { ...zero },
    myReaction: null,
    commentCount: 0,
    saved: false,
    editedAt: null,
    canEdit: mine && Date.now() - new Date(p.createdAt).getTime() < 24 * H,
    canDelete: mine,
    images: [],
    ...p,
  };
}

/**
 * The prototype's photos (FW-007) are the house paintings already in public/art (CC0, The Met; see
 * public/art/PROVENANCE.json), so the sample posts carry real photographs of real things without any
 * new imagery. Sizes are the files' own.
 */
const photo = (id: string, file: string, alt: string, width: number, height: number): PostImage => ({ id, url: `/art/${file}`, alt, width, height, contentType: "image/webp" });

const passage = (ref: string) => {
  const p = lookupPassage(ref)!;
  return { ref: { key: p.key, keyVersion: 1, label: p.label }, translation: p.translation, quote: p.text };
};

const today = new Date().toISOString().slice(0, 10);

type Store = {
  session: Session;
  posts: Post[];
  comments: Comment[];
  votd: Votd;
  friends: Friends;
  bios: Record<string, string | null>;
  blocked: Set<string>;
  /** Addresses with an account. Any other address signs up and goes through onboarding. */
  accounts: Set<string>;
  notifications: Notification[];
  taken: Set<string>;
  blockedUsers: UserCard[];
  fellowship: boolean;
  devices: Device[];
};

/** Google or Apple linked in the prototype: kept for the tab, since linking leaves the page and comes back. */
const mockLinked = {
  read(): Set<SocialProvider> {
    try {
      return new Set(JSON.parse(sessionStorage.getItem("bs.mock.linked") ?? "[]") as SocialProvider[]);
    } catch {
      return new Set();
    }
  },
  write(v: Set<SocialProvider>) {
    try {
      sessionStorage.setItem("bs.mock.linked", JSON.stringify([...v]));
    } catch {
      // Private window: kept for this page only.
    }
  },
  add(p: SocialProvider) {
    const v = this.read();
    v.add(p);
    this.write(v);
  },
  delete(p: SocialProvider) {
    const v = this.read();
    v.delete(p);
    this.write(v);
  },
  [Symbol.iterator]() {
    return this.read()[Symbol.iterator]();
  },
};

function seed(): Store {
  const posts: Post[] = [
    post({
      id: "p-ruth-1",
      author: ruth,
      kind: "verse",
      ...passage("Romans 8:28"),
      body: "Read this at the hospital this morning. Holding on to “all”.",
      reactions: { amen: 12, praying: 4, love: 0, insight: 0 },
      commentCount: 3,
      createdAt: ago(2 * H),
    }),
    post({
      id: "p-tom-1",
      author: tom,
      kind: "highlight",
      work: { id: "john-owen-works-vol-2", label: "John Owen · Communion with God", locator: "Part 1, Chapter 3" },
      source: { libraryId: "john-owen-works-vol-2", sectionUid: "communion-with-god-the-father-son-and-holy-ghost/7" },
      quote:
        "The greatest sorrow and burden you can lay upon the Father, the greatest unkindness you can do to him, is not to believe that he loves you.",
      body: "I keep coming back to this. Owen won’t let me treat the Father as reluctant.",
      reactions: { amen: 7, praying: 0, love: 0, insight: 5 },
      myReaction: "insight",
      commentCount: 1,
      createdAt: ago(5 * H),
    }),
    post({
      id: "p-grace-1",
      author: grace,
      kind: "note",
      title: "On waiting",
      body:
        "Isaiah says those who wait on the Lord renew their strength. I used to read that as patience, a kind of holding still.\n\nThis week it read differently. Waiting is not the absence of action; it is where the action is placed. The eagle does not flap; it is carried.\n\nI think I have been flapping for months. Trying to make the next thing happen, to arrange the outcome, to have an answer ready before the question is asked.\n\nThe verse doesn’t promise that the waiting will be short. It promises strength in it, and that we will walk and not faint, which is a smaller promise and a kinder one.\n\nSo: walking. Not running today. Walking, and not fainting.\n\nIf you’re in a season like this, I’d love to pray for you. Just say.",
      reactions: { amen: 9, praying: 3, love: 6, insight: 0 },
      commentCount: 2,
      saved: true,
      createdAt: ago(9 * H),
    }),
    post({
      id: "p-grace-2",
      author: grace,
      kind: "note",
      body: "Our walk after church: the river from the top of the hill, and the fields on the way down. Psalm 19 the whole way.",
      ref: { key: "Ps.19.1", keyVersion: 1, label: "Psalm 19:1" },
      images: [
        photo("img-grace-1", "cole-960.webp", "A river bending through green fields under a clearing storm, seen from a wooded hilltop", 960, 649),
        photo("img-grace-2", "ruisdael-960.webp", "A sandy road through ripe wheat fields under tall white clouds", 960, 736),
      ],
      reactions: { amen: 5, praying: 0, love: 7, insight: 0 },
      createdAt: ago(12 * H),
    }),
    post({
      id: "p-peter-1",
      author: peter,
      kind: "votd_repost",
      ...passage("Lamentations 3:22-23"),
      votdKey: "yesterday",
      body: "New every morning.",
      reactions: { amen: 15, praying: 0, love: 2, insight: 0 },
      createdAt: ago(26 * H),
    }),
    post({
      id: "p-me-1",
      author: me,
      kind: "verse",
      ...passage("Isaiah 40:31"),
      body: "For Grace, and anyone else walking rather than running this week.",
      reactions: { amen: 4, praying: 2, love: 1, insight: 0 },
      commentCount: 0,
      createdAt: ago(30 * H),
    }),
    post({
      id: "p-tom-2",
      author: tom,
      kind: "note",
      body: "Reading by one candle tonight while the power is out. It is very quiet, and Psalm 119:105 has never been so literal.",
      images: [photo("img-tom-1", "latour-1200.webp", "A single candle burning beside its reflection in a dark mirror", 1200, 1200)],
      reactions: { amen: 6, praying: 1, love: 2, insight: 0 },
      createdAt: ago(40 * H),
    }),
    post({
      id: "p-ruth-2",
      author: ruth,
      kind: "verse",
      ...passage("Psalm 46:1"),
      body: null,
      reactions: { amen: 8, praying: 0, love: 0, insight: 0 },
      createdAt: ago(50 * H),
    }),
  ];
  const comments: Comment[] = [
    { id: "c-1", postId: "p-ruth-1", author: tom, body: "Praying for your mum today.", createdAt: ago(100 * 60 * 1000), canDelete: false },
    { id: "c-2", postId: "p-ruth-1", author: grace, body: "“All” is doing so much work in that verse. Thank you for this.", createdAt: ago(80 * 60 * 1000), canDelete: false },
    { id: "c-3", postId: "p-ruth-1", author: me, body: "Amen. Text me if you need a lift this afternoon.", createdAt: ago(30 * 60 * 1000), canDelete: true },
    { id: "c-4", postId: "p-tom-1", author: ruth, body: "Which chapter is this in? I want to read the whole thing.", createdAt: ago(4 * H), canDelete: false },
    { id: "c-5", postId: "p-grace-1", author: peter, body: "Walking with you.", createdAt: ago(8 * H), canDelete: false },
    { id: "c-6", postId: "p-grace-1", author: ruth, body: "This was exactly what I needed today.", createdAt: ago(7 * H), canDelete: false },
  ];
  const p = lookupPassage("Philippians 4:6-7")!;
  return {
    session: { user: me, onboarded: true, email: "samuel@example.com", fellowshipEnabled: true, inviteCode: "samuel" },
    devices: [
      { id: "dev-web", device: typeof navigator !== "undefined" ? navigator.userAgent : "This browser", createdAt: daysAgoIso(12), lastUsedAt: new Date().toISOString(), expiresAt: daysAgoIso(-18), current: true },
      { id: "dev-ios", device: "BibleStandard/1.0 (iPhone; iOS 19.0)", createdAt: daysAgoIso(40), lastUsedAt: daysAgoIso(1), expiresAt: daysAgoIso(-20), current: false },
    ],
    posts,
    comments,
    votd: {
      key: today,
      ref: { key: p.key, label: p.label },
      translation: "WEB",
      quote: p.text,
      counts: { likes: 214, shares: 38, reposts: 17 },
      liked: false,
      reflection: "Let us bring today’s worries to God by name, and leave them with him, trusting his peace to keep what we cannot.",
      alongside: ["Matthew 6:25–34", "1 Peter 5:6–7", "Psalm 55:22"],
    },
    friends: { friends: [ruth, tom, grace, peter], incoming: [hannah], outgoing: [mark] },
    bios: {
      [me.id]: "Reading through the Psalms this autumn.",
      [ruth.id]: "Nurse. Leeds. Slowly reading Owen.",
      [tom.id]: "Puritans, coffee, and too many open tabs.",
      [grace.id]: "Learning to wait well.",
      [peter.id]: null,
      [hannah.id]: "New here.",
    },
    notifications: [
      { id: "n-0", kind: "plan_invite", actor: grace, others: 0, postId: null, journeyId: "j-psalms-grace", excerpt: null, detail: "The Psalms in a Month", createdAt: ago(20 * 60 * 1000), read: false },
      { id: "n-1", kind: "friend_request", actor: hannah, others: 0, postId: null, excerpt: null, createdAt: ago(40 * 60 * 1000), read: false },
      { id: "n-2", kind: "comment", actor: grace, others: 0, postId: "p-ruth-1", excerpt: "“All” is doing so much work in that verse.", createdAt: ago(80 * 60 * 1000), read: false },
      { id: "n-3", kind: "reaction", actor: ruth, others: 3, postId: "p-me-1", excerpt: null, reaction: "praying", createdAt: ago(20 * H), read: true },
      { id: "n-4", kind: "friend_accepted", actor: peter, others: 0, postId: null, excerpt: null, createdAt: ago(3 * 24 * H), read: true },
    ],
    blocked: new Set<string>(),
    blockedUsers: [],
    fellowship: true,
    accounts: new Set(["samuel@example.com"]),
    taken: new Set(["samuel", "ruth", "tom", "grace", "peter", "hannah", "mark", "admin", "fellowship", "biblestandard"]),
  };
}

const everyone = [me, ruth, tom, grace, peter, hannah, mark];

/** The prototype's reader and friends, for the other prototype sources (plans). */
export const mockPeople = { me, friends: [ruth, tom, grace, peter] };

/**
 * The word filter (PRD S2). In production the list is the API's blocked_term table, managed from the
 * moderation console; these few stand in for it. Matching ignores case, spacing and punctuation.
 */
const BLOCKED_TERMS = ["idiot", "stupid", "scam"];
export const FILTER_MESSAGE = "Some words in this aren’t allowed here. Your text is kept so you can change it.";
export function check(...texts: (string | null | undefined)[]) {
  for (const t of texts) {
    const words = (t ?? "").toLowerCase().split(/[^a-z]+/);
    if (words.some((w) => BLOCKED_TERMS.includes(w))) throw new Error(FILTER_MESSAGE);
  }
}
const DAY = 24 * H;

export function createMockSource(): FellowshipSource {
  const s = seed();
  /** Photos uploaded in this tab and not yet on a post (FW-007): object URLs, as the API's private URLs. */
  const uploads = new Map<string, ImageUpload & { used: boolean }>();
  // So the "new posts" line can be seen: a friend shares something 45 seconds after the page opens.
  setTimeout(() => {
    s.posts.unshift(
      post({
        id: "p-peter-live",
        author: peter,
        kind: "verse",
        ...passage("Matthew 11:28"),
        body: "Needed this on the train home.",
        createdAt: new Date().toISOString(),
      }),
    );
  }, 45_000);
  const find = (id: string) => s.posts.find((p) => p.id === id);
  const relationOf = (u: UserCard): Profile["relation"] =>
    u.id === me.id
      ? "self"
      : s.friends.friends.some((f) => f.id === u.id)
        ? "friends"
        : s.friends.outgoing.some((f) => f.id === u.id)
          ? "requested"
          : s.friends.incoming.some((f) => f.id === u.id)
            ? "incoming"
            : "none";
  const visible = (p: Post) =>
    p.author.id === me.id || (p.visibility === "friends" && relationOf(p.author) === "friends" && !s.blocked.has(p.author.id));

  // FS-136 details, kept as the API keeps them, and shown by its rule (access/profiles.ts profileDetails).
  const settings = new Map<string, ProfileSettings>([
    [me.id, { details: { ...NO_DETAILS }, locationVisibility: "friends", faithVisibility: "private", faithConsentAt: null }],
    [ruth.id, { details: { ...NO_DETAILS, city: "Leeds", country: "GB", tradition: "evangelical-protestant", church: "Grace Church" }, locationVisibility: "friends", faithVisibility: "friends", faithConsentAt: daysAgoIso(20) }],
    [tom.id, { details: { ...NO_DETAILS, city: "Bristol", country: "GB", tradition: "other-christian" }, locationVisibility: "friends", faithVisibility: "private", faithConsentAt: daysAgoIso(9) }],
  ]);
  const detailsFor = (u: UserCard, relation: Profile["relation"]): ProfileDetails => {
    const p = settings.get(u.id);
    if (!p) return { ...NO_DETAILS };
    const self = relation === "self";
    const friend = relation === "friends";
    const loc = self || (friend && p.locationVisibility === "friends");
    const faith = self || (friend && p.faithVisibility === "friends");
    return {
      city: loc ? p.details.city : null,
      country: loc ? p.details.country : null,
      tradition: faith ? p.details.tradition : null,
      denomination: faith ? p.details.denomination : null,
      church: faith ? p.details.church : null,
    };
  };
  const applyDetails = (input: ProfileDetailsInput): ProfileSettings => {
    const was = settings.get(me.id)!;
    // Left out keeps a value; null (or blank) clears it.
    const tidy = (v: string | null | undefined, was: string | null, max: number) => (v === undefined ? was : (v ?? "").replace(/\s+/g, " ").trim().slice(0, max) || null);
    const next: ProfileSettings = {
      details: {
        city: tidy(input.city, was.details.city, PROFILE_OPTIONS.limits.city),
        country: tidy(input.country, was.details.country, 2)?.toUpperCase() ?? null,
        tradition: input.tradition === undefined ? was.details.tradition : input.tradition,
        denomination: tidy(input.denomination, was.details.denomination, PROFILE_OPTIONS.limits.denomination),
        church: tidy(input.church, was.details.church, PROFILE_OPTIONS.limits.church),
      },
      locationVisibility: input.locationVisibility ?? was.locationVisibility,
      faithVisibility: input.faithVisibility ?? was.faithVisibility,
      faithConsentAt: was.faithConsentAt,
    };
    check(next.details.city ?? "", next.details.church ?? "");
    const holdsFaith = Boolean(next.details.tradition || next.details.denomination || next.details.church);
    if (!holdsFaith) next.faithConsentAt = null;
    else if (input.faithConsent) next.faithConsentAt ??= new Date().toISOString();
    else if (!next.faithConsentAt) throw new Error("Agree to keeping your tradition, denomination and church first.");
    settings.set(me.id, next);
    return next;
  };

  return {
    config: () =>
      delay({
        social: { state: "on" as const },
        features: { social: { state: "on" as const }, "social.images": { state: "on" as const }, sync: { state: "on" as const }, "web.purchases": { state: "off" as const } },
      }),

    session: () => delay(s.session?.onboarded ? { ...s.session, profile: settings.get(me.id)! } : s.session, 0),
    profileOptions: () => delay(PROFILE_OPTIONS, 0),
    async updateProfileDetails(input) {
      return delay(applyDetails(input));
    },
    sendCode: () => delay(undefined, 300),
    async verifyCode(email, code) {
      // Prototype: any six digits work. The API checks a hashed, 10-minute, 5-try code.
      if (!/^\d{6}$/.test(code)) throw new Error("That code isn’t right. Check the email and try again.");
      const isNew = !s.accounts.has(email.trim().toLowerCase());
      if (isNew) s.accounts.add(email.trim().toLowerCase());
      s.session = { user: me, onboarded: !isNew, email: email.trim().toLowerCase(), fellowshipEnabled: s.fellowship, inviteCode: me.handle };
      return delay({ isNew }, 300);
    },
    async signOut() {
      s.session = null;
      return delay(undefined, 50);
    },
    authProviders: () => delay({ google: true, apple: true }, 0),
    async startSocial(provider, { callbackURL, link }) {
      // The prototype has no provider: it signs in (or links) at once and goes straight back.
      if (link) mockLinked.add(provider);
      return delay(callbackURL, 200);
    },
    async signInMethods() {
      const email = s.session?.email ?? "samuel@example.com";
      return delay([
        { provider: "email" as const, email, linkedAt: "2026-09-01T09:00:00.000Z" },
        ...[...mockLinked].map((p) => ({ provider: p, email, linkedAt: "2026-10-01T09:00:00.000Z" })),
      ]);
    },
    async removeSignInMethod(provider) {
      mockLinked.delete(provider);
      return delay(undefined);
    },
    devices: () => delay(s.devices),
    async signOutDevice(id) {
      s.devices = s.devices.filter((d) => d.id !== id || d.current);
      return delay(undefined);
    },
    async signOutOtherDevices() {
      s.devices = s.devices.filter((d) => d.current);
      return delay(undefined);
    },
    handleAvailable: (h) =>
      BLOCKED_TERMS.some((w) => h.toLowerCase().includes(w)) ? delay(false, 200) : delay(!s.taken.has(h.toLowerCase()) || h.toLowerCase() === s.session?.user.handle, 200),
    async setAvatar(dataUrl) {
      me.avatarUrl = dataUrl;
      return delay(undefined);
    },
    async completeOnboarding({ handle, displayName, fellowshipEnabled, details }) {
      if (details) applyDetails(details);
      check(handle.replace(/_/g, " "), displayName);
      me.handle = handle;
      me.displayName = displayName;
      s.fellowship = fellowshipEnabled;
      s.session = { ...s.session!, user: me, onboarded: true, fellowshipEnabled, inviteCode: handle };
      return delay(undefined);
    },
    async updateProfile({ displayName, bio }) {
      check(displayName, bio);
      me.displayName = displayName;
      s.bios[me.id] = bio || null;
      return delay(undefined);
    },
    async setFellowship(on) {
      s.fellowship = on;
      if (s.session) s.session = { ...s.session, fellowshipEnabled: on };
      return delay(undefined);
    },
    async exportData() {
      return delay({
        exportedAt: new Date().toISOString(),
        profile: { ...me, bio: s.bios[me.id] ?? null, ...settings.get(me.id) },
        posts: s.posts.filter((p) => p.author.id === me.id),
        comments: s.comments.filter((c) => c.author.id === me.id),
        saved: s.posts.filter((p) => p.saved).map((p) => p.id),
        friends: s.friends.friends.map((f) => f.handle),
      });
    },
    async deleteAccount() {
      s.session = null;
      return delay(undefined, 400);
    },

    votd: () => delay(s.votd),
    async likeVotd(liked) {
      if (liked !== s.votd.liked) s.votd.counts.likes += liked ? 1 : -1;
      s.votd.liked = liked;
      return delay(s.votd);
    },
    async shareVotd() {
      s.votd.counts.shares += 1;
      return delay(s.votd, 60);
    },
    async feed() {
      const items = s.posts.filter(visible).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      return delay({ items, nextCursor: null });
    },
    newCount: (since) => delay(s.posts.filter((p) => visible(p) && p.author.id !== me.id && p.createdAt > since).length, 40),
    post: (id) => delay(find(id) && visible(find(id)!) ? find(id)! : null),
    comments: (postId) =>
      delay(
        s.comments
          .filter((c) => c.postId === postId)
          .map((c) => ({ ...c, canDelete: c.author.id === me.id || find(postId)?.author.id === me.id })),
      ),
    async uploadPostImage(image, onProgress) {
      if (!["image/webp", "image/jpeg", "image/png"].includes(image.type)) throw new ApiError("That kind of file isn’t accepted. Use a JPEG, PNG or WebP photo.", "unsupported-media-type", 415);
      if (image.size > LIMITS.imageBytes) throw new ApiError("That photo is too large. Try a smaller one.", "payload-too-large", 413);
      const bitmap = await createImageBitmap(image);
      const { width, height } = bitmap;
      bitmap.close();
      // As a phone connection would: the bar moves, then the server answers.
      for (const f of [0.2, 0.45, 0.7, 0.9, 1]) {
        await new Promise((r) => setTimeout(r, 60));
        onProgress?.(f);
      }
      const id = `img-${crypto.randomUUID()}`;
      const up = { id, url: URL.createObjectURL(image), width, height, contentType: image.type as ImageUpload["contentType"], bytes: image.size, expiresAt: new Date(Date.now() + DAY).toISOString() };
      uploads.set(id, { ...up, used: false });
      return up;
    },
    async createPost(input) {
      check(input.body, input.kind === "note" ? input.title : null);
      const images = input.images ?? [];
      if (images.length > LIMITS.images) throw new ApiError("Up to four photos on a post.", "validation", 400);
      const unavailable = images.map((im, i) => (uploads.get(im.id)?.used === false ? null : `images.${i}.id`)).filter((p): p is string => !!p);
      if (unavailable.length) throw new ApiError("A photo had to be sent again. Try posting once more.", "image-unavailable", 422, unavailable);
      for (const im of images) {
        if (!im.alt.trim() || im.alt.length > LIMITS.alt) throw new ApiError("Describe each photo in a few words, up to 500 characters.", "validation", 400);
        check(im.alt);
      }
      const attach = (p: Post): Post => {
        p.images = images.map((im) => {
          const up = uploads.get(im.id)!;
          up.used = true;
          return { id: up.id, url: up.url, alt: im.alt.trim(), width: up.width, height: up.height, contentType: up.contentType };
        });
        return p;
      };
      const base = { id: `p-${crypto.randomUUID()}`, author: me, createdAt: new Date().toISOString(), visibility: input.visibility };
      let p: Post;
      if (input.kind === "verse") {
        const passageFound = await lookupWeb(input.refKey, 5);
        if (!passageFound) throw new Error("We can’t find that passage.");
        const full = { ref: { key: passageFound.key, keyVersion: 1, label: passageFound.label }, translation: passageFound.translation, quote: passageFound.text };
        // A chosen phrase must come from the passage itself; the server will check the same.
        const quote = input.quote && passageFound.text.includes(input.quote.replace(/^… | …$/g, "")) ? input.quote : full.quote;
        p = post({ ...base, kind: "verse", ...full, quote, body: input.body || null });
      } else if (input.kind === "note") {
        const anchor = input.refKey ? await lookupWeb(input.refKey, 5) : null;
        p = post({
          ...base,
          kind: "note",
          title: input.title || null,
          body: input.body,
          ref: anchor ? { key: anchor.key, keyVersion: 1, label: anchor.label } : null,
        });
      } else if (input.kind === "highlight") {
        if (input.quote.length > LIMITS.quote) throw new Error("That’s too long to quote. Choose a shorter passage.");
        check(input.quote);
        p = post({ ...base, kind: "highlight", work: input.work, source: input.source, quote: input.quote, body: input.body || null });
      } else if (input.kind === "milestone") {
        p = post({ ...base, kind: "milestone", title: input.title, body: input.body || null });
      } else {
        p = post({
          ...base,
          kind: "votd_repost",
          ref: { ...s.votd.ref, keyVersion: 1 },
          translation: s.votd.translation,
          quote: s.votd.quote,
          votdKey: s.votd.key,
          body: input.body || null,
        });
        s.votd.counts.reposts += 1;
      }
      s.posts.unshift(attach(p));
      return delay(p, 250);
    },
    async editPost(id, input) {
      const p = find(id);
      if (!p || p.author.id !== me.id) throw new Error("This post isn’t available.");
      if (Date.now() - new Date(p.createdAt).getTime() > DAY) throw new Error("Posts can be edited for 24 hours after posting.");
      check(input.body, input.title);
      p.body = input.body.trim() || null;
      if (p.kind === "note" && input.title !== undefined) p.title = input.title.trim() || null;
      p.editedAt = new Date().toISOString();
      return delay(p, 200);
    },
    async passagePosts(refKey) {
      const key = (await lookupWeb(refKey))?.key ?? refKey;
      return delay(s.posts.filter((p) => p.ref?.key === key && visible(p)));
    },
    async deletePost(id) {
      s.posts = s.posts.filter((p) => p.id !== id || p.author.id !== me.id);
      return delay(undefined);
    },
    async react(postId, reaction: Reaction | null) {
      const p = find(postId)!;
      if (p.myReaction) p.reactions[p.myReaction] -= 1;
      if (reaction) p.reactions[reaction] += 1;
      p.myReaction = reaction;
      return delay(p, 80);
    },
    async setSaved(postId, saved) {
      const p = find(postId)!;
      p.saved = saved;
      return delay(p, 80);
    },
    async addComment(postId, body) {
      check(body);
      const c: Comment = { id: `c-${crypto.randomUUID()}`, postId, author: me, body, createdAt: new Date().toISOString(), canDelete: true };
      s.comments.push(c);
      find(postId)!.commentCount += 1;
      return delay(c, 200);
    },
    async deleteComment(id) {
      const c = s.comments.find((x) => x.id === id);
      if (c && (c.author.id === me.id || find(c.postId)?.author.id === me.id)) {
        s.comments = s.comments.filter((x) => x.id !== id);
        find(c.postId)!.commentCount -= 1;
      }
      return delay(undefined);
    },
    saved: () => delay(s.posts.filter((p) => p.saved && visible(p))),

    async profile(handle) {
      const u = everyone.find((x) => x.handle === handle.toLowerCase());
      if (!u || s.blocked.has(u.id)) return delay(null);
      const friendCount = u.id === me.id ? s.friends.friends.length : 12 + u.handle.length * 3;
      const relation = relationOf(u);
      return delay({ ...u, bio: s.bios[u.id] ?? null, friendCount, relation, details: detailsFor(u, relation) });
    },
    async profilePosts(handle) {
      const u = everyone.find((x) => x.handle === handle.toLowerCase());
      if (!u) return delay([]);
      return delay(s.posts.filter((p) => p.author.id === u.id && visible(p)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    },
    friends: () => delay(s.friends),
    async findPeople(query) {
      const q = query.trim().replace(/^@/, "").toLowerCase();
      if (!q) return delay([]);
      return delay(everyone.filter((u) => u.id !== me.id && !s.blocked.has(u.id) && u.handle.startsWith(q)).map((u) => ({ ...u, relation: relationOf(u) })));
    },
    async requestFriend(userId) {
      const u = everyone.find((x) => x.id === userId)!;
      if (relationOf(u) === "none") s.friends.outgoing.push(u);
      return delay(undefined);
    },
    async requestFriendByInvite(code) {
      const u = everyone.find((x) => x.handle === code.toLowerCase());
      if (!u) throw new Error("This invite link doesn’t work any more. Ask your friend for a new one.");
      if (relationOf(u) === "incoming") return this.acceptFriend(u.id);
      if (relationOf(u) === "none") s.friends.outgoing.push(u);
      return delay(undefined);
    },
    async acceptFriend(userId) {
      const u = s.friends.incoming.find((x) => x.id === userId);
      if (u) {
        s.friends.incoming = s.friends.incoming.filter((x) => x.id !== userId);
        s.friends.friends.push(u);
      }
      return delay(undefined);
    },
    async declineFriend(userId) {
      s.friends.incoming = s.friends.incoming.filter((x) => x.id !== userId);
      s.friends.outgoing = s.friends.outgoing.filter((x) => x.id !== userId);
      return delay(undefined);
    },
    async removeFriend(userId) {
      s.friends.friends = s.friends.friends.filter((x) => x.id !== userId);
      return delay(undefined);
    },

    async block(userId) {
      s.blocked.add(userId);
      const who = everyone.find((x) => x.id === userId);
      if (who && !s.blockedUsers.some((x) => x.id === userId)) s.blockedUsers.push(who);
      s.friends.friends = s.friends.friends.filter((x) => x.id !== userId);
      s.friends.incoming = s.friends.incoming.filter((x) => x.id !== userId);
      s.friends.outgoing = s.friends.outgoing.filter((x) => x.id !== userId);
      s.comments = s.comments.filter((c) => c.author.id !== userId);
      return delay(undefined);
    },

    blocks: () => delay(s.blockedUsers),
    async unblock(userId) {
      s.blocked.delete(userId);
      s.blockedUsers = s.blockedUsers.filter((x) => x.id !== userId);
      return delay(undefined);
    },

    unreadCount: () => delay(s.notifications.filter((n) => !n.read).length, 40),
    async notifications() {
      const out = structuredClone(s.notifications);
      for (const n of s.notifications) n.read = true;
      return delay(out);
    },
    report: () => delay(undefined, 250),

    lookupPassage: (ref, maxVerses) => lookupWeb(ref, maxVerses),
  };
}
